import { useState, useEffect, useMemo, useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import styled from 'styled-components';
import {
  MozartCheckBox,
  MozartLoading,
  MozartSelectField,
} from '@pgforsta/mozart_ui';
import { MozartButton } from '@pgforsta/mozart_ui/lib/v4';
import { cloneDeep } from 'lodash';
import Layout from '../Layout/Layout';
import VerifyEntityModal from '../../components/VerifySources/VerifyEntityModal';
import UrlFinderModal from '../../components/UrlFinderModal/UrlFinderModal';
import ReviewsModal from '../../components/VerifySources/ReviewsModal';
import HistoryModal from '../../components/VerifySources/HistoryModal';
import DeleteModal from '../../components/VerifySources/DeleteModal';
import AddSourcesErrorsModal from '../../components/VerifySources/AddSourcesErrorsModal';
import * as SourcesTable from '../../components/VerifySources/SourcesTable';
import { Card, CardHeader, CardContent } from '../../components/styled/Card';
import { ActionButton, ActionIcon } from '../../components/styled/ActionButton';
import CheckboxCell from '../../components/styled/CheckboxCell';
import ButtonGroup from '../../components/styled/ButtonGroup';
import TextInput from '../../components/styled/TextInput';
import { useAuth } from '../../context/auth';
import useAsync from '../../hooks/async';
import useCheckboxes from '../../hooks/checkboxes';
import useUrlFinder from '../../hooks/url-finder';
import { getDetails, getClientDetails } from '../../api/services/entities';
import { getIndustries } from '../../api/services/industries';
import {
  getSuggestedSources,
  updateSourceVerifiedStatus,
  getEntityVerificationSources,
  addSources,
  getAutoSelectSources,
} from '../../api/services/sources';
import { extractHostname } from '../../utils/functions';

const CustomCard = styled(Card)`
  margin-top: 20px;
  overflow: hidden;
`;

const CustomCardContent = styled(CardContent)`
  padding: ${(props) => props.padding || '20px'};
  font-size: ${(props) => props.fontSize || '14px'};
`;

const EntityDetailsHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const EntityDetails = styled.div`
  display: flex;
`;

const Detail = styled.div`
  box-sizing: border-box;
  width: ${(props) => props.width || 'auto'};

  &:not(:last-child) {
    margin-right: 30px;
  }
`;

const DetailLabel = styled.div`
  margin-bottom: 2px;
  font-size: 10px;
  text-transform: uppercase;
`;

const DetailValue = styled.div`
  font-size: 14px;
`;

const Divider = styled.hr`
  margin: 20px 0;
  border: none;
  border-top: 1px solid #d9d5f9;
`;

const ClientDetailsSection = styled.div`
  margin-top: 15px;
`;

const ClientDetailsSectionTitle = styled.h4`
  margin: 0 0 10px 0;
  font-size: 14px;
  font-weight: 600;
  color: #333;
`;

const ClientCard = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 12px;
  margin-bottom: 10px;
  background-color: #fafafa;
`;

const ClientHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const ClientId = styled.span`
  font-weight: 600;
  font-size: 15px;
  color: #5187ec;
`;

const ClientStatus = styled.span`
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 12px;
  background-color: ${(props) => (props.active ? '#e8f5e9' : '#ffebee')};
  color: ${(props) => (props.active ? '#2e7d32' : '#c62828')};
`;

const ClientDetailsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
`;

const ClientDetailItem = styled.div`
  min-width: 120px;
`;

const ClientDetailLabel = styled.div`
  margin-bottom: 2px;
  font-size: 11px;
  text-transform: uppercase;
  color: #666;
`;

const ClientDetailValue = styled.div`
  font-size: 14px;
  color: #333;
`;

const ProviderLocationsSection = styled.div`
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed #e0e0e0;
`;

const ProviderLocationTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 10px;
  color: #333;
`;

const ProviderLocationCard = styled.div`
  background-color: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 12px;
  margin-bottom: 10px;
`;

const LocationHeader = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #5187ec;
  margin-bottom: 10px;
`;

const LocationDetailsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
`;

const LocationDetailItem = styled.div`
  min-width: 120px;
`;

const LocationDetailLabel = styled.div`
  margin-bottom: 2px;
  font-size: 11px;
  text-transform: uppercase;
  color: #666;
`;

const LocationDetailValue = styled.div`
  font-size: 14px;
  color: #333;
`;

const VerifyButtonWrapper = styled.span`
  button:disabled {
    color: #ffffff;
    background-color: #b0a9f9;
  }
`;

const UrlCell = styled.div`
  display: flex;
  align-items: center;
`;

const UrlIconWrapper = styled.div`
  width: 8px;
  height: 8px;
  margin-right: 5px;
  padding: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 99px;
  background-color: ${(props) => props.color};

  i,
  span {
    color: #ffffff;
    font-size: 8px;
  }

  span {
    font-weight: 600;
    min-width: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const UrlLink = styled.a`
  color: #5187ec;
  font-size: 12px;
  text-decoration: none;
`;

const CopyIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 6px;
  cursor: pointer;
  color: #5187ec;
  vertical-align: middle;

  &:hover {
    color: #3e3a83;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const CopyFeedback = styled.span`
  font-size: 11px;
  color: #92d048;
  margin-left: 4px;
`;

const ActionButtons = styled.div`
  display: flex;

  & > *:not(:last-child) {
    margin-right: 2px;
  }
`;

const SelectedActions = styled.div`
  padding: 15px;
  padding-left: 67px;
  border-top: 1px solid #d9d5f9;
`;

const SuggestedSource = styled.span`
  color: #5187ec;
`;

const SuggestedSourceLink = styled.a`
  text-decoration: none;

  &,
  &:hover,
  &:active,
  &:visited {
    color: inherit;
  }
`;

const ListLabel = styled.p`
  margin-top: 20px;
  color: #3e3a83;
`;

const iconColor = {
  green: '#92d048',
  yellow: '#f7cb5a',
  red: '#eb392b',
  black: '#000000',
};

const sourceStatus = {
  verify: 'verify',
  undo: 'undo',
};

const sourceHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
};

const arrowIconStyle = {
  color: '#262626',
  fontSize: '7px',
  cursor: 'pointer',
  marginLeft: '8px',
};

const initialNewSourcesValue = [
  { sourceId: '', url: '', industryId: '', manualSourceSelect: false },
  { sourceId: '', url: '', industryId: '', manualSourceSelect: false },
  { sourceId: '', url: '', industryId: '', manualSourceSelect: false },
  { sourceId: '', url: '', industryId: '', manualSourceSelect: false },
  { sourceId: '', url: '', industryId: '', manualSourceSelect: false },
  { sourceId: '', url: '', industryId: '', manualSourceSelect: false },
  { sourceId: '', url: '', industryId: '', manualSourceSelect: false },
  { sourceId: '', url: '', industryId: '', manualSourceSelect: false },
  { sourceId: '', url: '', industryId: '', manualSourceSelect: false },
  { sourceId: '', url: '', industryId: '', manualSourceSelect: false },
];

const DATE_FORMAT = 'do LLLL y';
const VERIFIED_DATE_FORMAT = 'MM/dd/y h:mm:ss a';
const APPROVED = 'Approved';
const GOOGLE_SEARCH_URL = 'https://www.google.com/search';

const VerifySources = () => {
  const history = useHistory();
  const { entityId } = useParams();
  const { user } = useAuth();

  const entityQuery = useAsync();
  const verifyQuery = useAsync();
  const domainMappingQuery = useAsync();
  const addSourcesQuery = useAsync();
  const autoSelectSourcesQuery = useAsync();
  const suggestedSourcesQuery = useAsync();
  const industriesQuery = useAsync();
  const urlFinder = useUrlFinder();

  const [showVerifyEntityModal, setShowVerifyEntityModal] = useState(false);
  const [activeSourceReviews, setActiveSourceReviews] = useState(null);
  const [activeSourceHistory, setActiveSourceHistory] = useState(null);
  const [deletableHaversterIds, setDeletableHarvesterIds] = useState([]);
  const [isAddingSources, setIsAddingSources] = useState(false);
  const [newSources, setNewSources] = useState(initialNewSourcesValue);
  const [newSourcesErrors, setNewSourcesErrors] = useState({});
  const [factor, setNewFactor] = useState(1);
  const [clientDetails, setClientDetails] = useState([]);
  const [isClientDetailsLoading, setIsClientDetailsLoading] = useState(true);
  const [copiedField, setCopiedField] = useState(null);

  const handleCopyAddress = useCallback((text, fieldKey) => {
    if (!text || text === '--') return;
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(fieldKey);
      setTimeout(() => setCopiedField(null), 2000);
    });
  }, []);

  const { data: entityDetails, run: entityQueryRun } = entityQuery;
  const { run: suggestedSourcesQueryRun } = suggestedSourcesQuery;
  const { run: domainMappingQueryRun } = domainMappingQuery;
  const { run: industriesQueryRun } = industriesQuery;

  const isEntityVerified = entityDetails?.verificationStatus === APPROVED;
  const entitySources = entityDetails
    ? entityDetails.entityProfileDtoList || []
    : [];
  const checkboxes = useCheckboxes(entitySources);
  var sortedEntitySources = entitySources.sort(
    (a, b) => factor * a.sourceName.localeCompare(b.sourceName)
  );
  useEffect(() => {
    entityQueryRun(getDetails(entityId)).then(({ type, id }) => {
      if (type && id) {
        suggestedSourcesQueryRun(getSuggestedSources(type, id));
      }
    });
    // Fetch client details separately
    setIsClientDetailsLoading(true);
    getClientDetails(entityId)
      .then((data) => setClientDetails(data || []))
      .catch((err) => {
        console.error('Error fetching client details:', err);
        setClientDetails([]);
      })
      .finally(() => setIsClientDetailsLoading(false));
  }, [entityQueryRun, entityId, suggestedSourcesQueryRun]);

  useEffect(() => {
    domainMappingQueryRun(getEntityVerificationSources());
  }, [domainMappingQueryRun]);

  useEffect(() => {
    industriesQueryRun(getIndustries(entityId));
  }, [industriesQueryRun, entityId]);

  const getDefaultIndustryId = useCallback(() => {
    if (clientDetails.length > 0 && clientDetails[0].industryId) {
      return clientDetails[0].industryId;
    }
    return 'healthcare';
  }, [clientDetails]);

  useEffect(() => {
    if (!industriesQuery.isResolved) {
      return;
    }
    const defaultIndustryId = getDefaultIndustryId();
    setNewSources((prevNewSources) =>
      prevNewSources.map((prevNewSource) => ({
        ...prevNewSource,
        industryId: defaultIndustryId,
      }))
    );
  }, [industriesQuery.isResolved, getDefaultIndustryId]);

  const industriesOptions = useMemo(
    () =>
      (industriesQuery.data || []).map(({ id, name }) => ({
        value: id,
        text: name,
      })),
    [industriesQuery.data]
  );

  const allDomainValues = useMemo(() => {
    if (!domainMappingQuery.isResolved) {
      return [];
    }
    return Object.values(domainMappingQuery.data)
      .flat()
      .filter(
        (source, index, self) =>
          self.findIndex((s) => source.sourceId === s.sourceId) === index
      );
  }, [domainMappingQuery.isResolved, domainMappingQuery.data]);

  const hasAtLeastOneValidNewSource = useMemo(
    () =>
      newSources.some(
        ({ sourceId, url, industryId }) =>
          Boolean(sourceId) && Boolean(url.trim()) && Boolean(industryId)
      ),
    [newSources]
  );

  const allDomainValuesOptions = useMemo(
    () =>
      allDomainValues.map((domain) => ({
        value: domain.sourceId,
        text: domain.sourceName,
      })),
    [allDomainValues]
  );

  const verifyButtonTitle = useMemo(
    () =>
      isEntityVerified
        ? `Verified by '${entityDetails.verifiedBy}' on ${format(
            new Date(entityDetails.verifiedTime),
            VERIFIED_DATE_FORMAT
          )}`
        : '',
    [isEntityVerified, entityDetails]
  );

  const handleAddSourceFieldChange = (field, value, index) => {
    const newSourcesCopy = cloneDeep(newSources);
    newSourcesCopy[index][field] = value;
    if (field === 'sourceId') {
      newSourcesCopy[index].manualSourceSelect = true;
    }
    setNewSources(newSourcesCopy);
  };

  const handleUrlFieldBlur = (value, index) => {
    if (!value || newSources[index].manualSourceSelect) {
      return;
    }
    const hostName = extractHostname(value);

    autoSelectSourcesQuery
      .run(getAutoSelectSources(hostName))
      .then((autoSelectRes) => {
        if (autoSelectRes?.[0]?.sourceId) {
          const newSourcesCopy = cloneDeep(newSources);
          newSourcesCopy[index].sourceId = autoSelectRes[0].sourceId;
          setNewSources(newSourcesCopy);
        }
      });
  };

  const resetNewSourcesToInitialValue = () => {
    const defaultIndustryId = getDefaultIndustryId();
    setNewSources(
      initialNewSourcesValue.map((initialNewSource) => ({
        ...initialNewSource,
        industryId: defaultIndustryId,
      }))
    );
  };

  const resetAddSources = () => {
    setIsAddingSources(false);
    setNewSourcesErrors({});
    resetNewSourcesToInitialValue();
    entityQueryRun(getDetails(entityId)).then(({ type, id }) => {
      if (type && id) {
        suggestedSourcesQueryRun(getSuggestedSources(type, id));
      }
    });
  };

  const saveNewSources = () => {
    const data = newSources
      .filter(
        ({ sourceId, url, industryId }) =>
          Boolean(sourceId) && Boolean(url.trim()) && Boolean(industryId)
      )
      .map(({ sourceId, url, industryId }) => ({
        entityId: Number(entityId),
        sourceId,
        url: url.trim(),
        industryId,
      }));
    addSourcesQuery.run(addSources(data)).then((res) => {
      if (Object.keys(res).length === 0) {
        resetAddSources();
      } else {
        setNewSourcesErrors(res);
      }
    });
  };

  const handleAllSourceCheck = (event) => {
    checkboxes.toggleAll(event.target.checked);
  };

  const handleSourceCheck = (checked, source) => {
    checkboxes.toggleItem(
      checked,
      source,
      ({ harvesterId }) => harvesterId !== source.harvesterId
    );
  };

  const checkedHarvesterIds = useMemo(
    () => checkboxes.checkedItems.map(({ harvesterId }) => harvesterId),
    [checkboxes.checkedItems]
  );

  const verifyCheckedSources = () => {
    runVerifyQuery({
      harvesterIds: checkedHarvesterIds,
      status: sourceStatus.verify,
    });
  };

  const toggleSourceVerifiedStatus = (harvesterId, isVerified) => {
    runVerifyQuery({
      harvesterIds: [harvesterId],
      status: isVerified ? sourceStatus.undo : sourceStatus.verify,
    });
  };

  const runVerifyQuery = (data) => {
    verifyQuery.run(updateSourceVerifiedStatus(data)).then(() => {
      entityQueryRun(getDetails(entityId));
      checkboxes.resetCheckedItems();
    });
  };

  const handleDeleteConfirm = () => {
    setDeletableHarvesterIds([]);
    entityQueryRun(getDetails(entityId));
    checkboxes.resetCheckedItems();
  };

  const handleReviewsModalClose = (hasDeletedReviews) => {
    setActiveSourceReviews(null);
    if (hasDeletedReviews) {
      entityQueryRun(getDetails(entityId));
    }
  };

  const handleVerifyEntityClose = (isVerified = false) => {
    setShowVerifyEntityModal(false);
    if (isVerified) {
      entityQueryRun(getDetails(entityId));
    }
  };

  const handleTriggerUrlFinderConfirm = () => {
    urlFinder.confirm();
    urlFinder.trigger({
      userEmailId: user.email,
      entityIds: [Number(entityId)],
    });
  };

  const getHeaders = () => (
    <SourcesTable.Row>
      <SourcesTable.Header>
        <CheckboxCell>
          <MozartCheckBox>
            <input
              id="select-all"
              name="select-all"
              type="checkbox"
              checked={checkboxes.allChecked}
              onChange={handleAllSourceCheck}
            />
            <label htmlFor="select-all" />
          </MozartCheckBox>
        </CheckboxCell>
      </SourcesTable.Header>
      <SourcesTable.Header>
        <div style={sourceHeaderStyle}>
          <div>Source</div>
          <div style={arrowIconStyle}>
            <i
              className="icon-UpAlternative"
              onClick={() => {
                setNewFactor(1);
              }}
            />
            <br />
            <i
              className="icon-DownAlternative"
              onClick={() => {
                setNewFactor(-1);
              }}
            />
          </div>
        </div>
      </SourcesTable.Header>
      <SourcesTable.Header>URL</SourcesTable.Header>
      <SourcesTable.Header>Place ID</SourcesTable.Header>
      <SourcesTable.Header>Scope</SourcesTable.Header>
      <SourcesTable.Header>Address</SourcesTable.Header>
      <SourcesTable.Header>
        Document <br /> Count
      </SourcesTable.Header>
      <SourcesTable.Header>
        Expected <br /> Count
      </SourcesTable.Header>
      <SourcesTable.Header>Actions</SourcesTable.Header>
    </SourcesTable.Row>
  );

  const getRows = () => {
    const rows = sortedEntitySources.map((source) => {
      const {
        verificationFlag: isVerified,
        activeFlag: isActive,
        harvesterId,
        docCount,
        expectedReviewCount,
      } = source;
      const checkboxId = `select-${harvesterId}`;
      const countMismatch =
        docCount >= 0 &&
        expectedReviewCount >= 0 &&
        docCount !== expectedReviewCount;
      return (
        <SourcesTable.Row key={harvesterId} mismatch={countMismatch}>
          <SourcesTable.Cell>
            <CheckboxCell>
              <MozartCheckBox>
                <input
                  type="checkbox"
                  id={checkboxId}
                  name={checkboxId}
                  disabled={!isActive}
                  checked={checkedHarvesterIds.includes(harvesterId)}
                  onChange={(event) =>
                    handleSourceCheck(event.target.checked, source)
                  }
                />
                <label htmlFor={checkboxId} />
              </MozartCheckBox>
            </CheckboxCell>
          </SourcesTable.Cell>
          <SourcesTable.Cell>{source.sourceName}</SourcesTable.Cell>
          <SourcesTable.Cell>
            <UrlCell>
              <UrlIconWrapper
                color={isVerified ? iconColor.green : iconColor.yellow}
              >
                {isVerified ? <i className="icon-Check" /> : <span>!</span>}
              </UrlIconWrapper>
              <UrlLink
                href={source.url}
                target="_blank"
                rel="noreferrer noopener"
              >
                {source.url}
              </UrlLink>
            </UrlCell>
          </SourcesTable.Cell>
          <SourcesTable.Cell>{source.placeId || '--'}</SourcesTable.Cell>
          <SourcesTable.Cell>{source.productId || '--'}</SourcesTable.Cell>
          <SourcesTable.Cell>{source.address || '--'}</SourcesTable.Cell>
          <SourcesTable.Cell>{docCount}</SourcesTable.Cell>
          <SourcesTable.Cell>{expectedReviewCount}</SourcesTable.Cell>
          <SourcesTable.Cell>
            <ActionButtons>
              <ActionButton
                disabled={!isActive}
                onClick={() => setActiveSourceReviews(source)}
              >
                Reviews
                <ActionIcon className="icon-Feedback" />
              </ActionButton>
              <ActionButton
                disabled={!isActive}
                onClick={() =>
                  toggleSourceVerifiedStatus(harvesterId, isVerified)
                }
              >
                {isVerified ? 'Undo' : 'Verify'}
                <ActionIcon
                  className={isVerified ? 'icon-Undo' : 'icon-Check'}
                />
              </ActionButton>
              <ActionButton
                disabled={!isActive}
                onClick={() => setActiveSourceHistory(source)}
              >
                History
                <ActionIcon className="icon-History" />
              </ActionButton>
              <ActionButton
                title={!isActive ? 'Deletion is in progress' : ''}
                disabled={!isActive}
                color={iconColor.black}
                onClick={() => setDeletableHarvesterIds([harvesterId])}
              >
                Delete
                <ActionIcon className="icon-Delete" color={iconColor.red} />
              </ActionButton>
            </ActionButtons>
          </SourcesTable.Cell>
        </SourcesTable.Row>
      );
    });

    if (isAddingSources) {
      const addSourcesRows = newSources.map(
        ({ sourceId, url, industryId }, index) => (
          <SourcesTable.Row key={index}>
            <SourcesTable.Cell />
            <SourcesTable.Cell>
              <MozartSelectField
                defaultValue={sourceId}
                data={allDomainValuesOptions}
                onChange={(value) =>
                  handleAddSourceFieldChange('sourceId', value, index)
                }
                searchable
                width="180px"
              />
            </SourcesTable.Cell>
            <SourcesTable.Cell>
              <TextInput
                value={url}
                onChange={(event) =>
                  handleAddSourceFieldChange('url', event.target.value, index)
                }
                onBlur={(event) =>
                  handleUrlFieldBlur(event.target.value, index)
                }
              />
            </SourcesTable.Cell>
            <SourcesTable.Cell colSpan="2">
              <MozartSelectField
                defaultValue={industryId}
                data={industriesOptions}
                onChange={(value) =>
                  handleAddSourceFieldChange('industryId', value, index)
                }
                searchable
                hintText="Industry"
              />
            </SourcesTable.Cell>
            <SourcesTable.Cell />
            <SourcesTable.Cell />
          </SourcesTable.Row>
        )
      );

      return addSourcesRows.concat(rows);
    }

    return rows;
  };

  const renderClientDetails = () => {
    if (!clientDetails || clientDetails.length === 0) {
      return null;
    }

    // Check if this is a provider (Person) or location
    const isProvider = entityDetails?.type === 'Person';

    return (
      <ClientDetailsSection>
        <ClientDetailsSectionTitle>
          Client-Specific Details ({clientDetails.length} client
          {clientDetails.length > 1 ? 's' : ''})
        </ClientDetailsSectionTitle>
        {clientDetails.map((client, index) => (
          <ClientCard key={client.clientId || index}>
            <ClientHeader>
              <ClientId>
                Client: {client.clientName || client.clientId} (
                {client.clientId})
                {client.industryId ? ` (${client.industryId})` : ''}
              </ClientId>
              <ClientStatus active={client.active}>
                {client.active ? 'Active' : 'Inactive'}
              </ClientStatus>
            </ClientHeader>
            <ClientDetailsGrid>
              {isProvider && (
                <ClientDetailItem>
                  <ClientDetailLabel>NPI:</ClientDetailLabel>
                  <ClientDetailValue>
                    {entityDetails?.code || '--'}
                  </ClientDetailValue>
                </ClientDetailItem>
              )}
              <ClientDetailItem>
                <ClientDetailLabel>Display Name:</ClientDetailLabel>
                <ClientDetailValue>
                  {client.displayName || '--'}
                </ClientDetailValue>
              </ClientDetailItem>
              <ClientDetailItem>
                <ClientDetailLabel>Internal Name:</ClientDetailLabel>
                <ClientDetailValue>
                  {client.internalName || '--'}
                </ClientDetailValue>
              </ClientDetailItem>
              <ClientDetailItem>
                <ClientDetailLabel>Phone:</ClientDetailLabel>
                <ClientDetailValue>{client.phone || '--'}</ClientDetailValue>
              </ClientDetailItem>
              <ClientDetailItem>
                <ClientDetailLabel>Email:</ClientDetailLabel>
                <ClientDetailValue>{client.email || '--'}</ClientDetailValue>
              </ClientDetailItem>
              <ClientDetailItem>
                <ClientDetailLabel>Website:</ClientDetailLabel>
                <ClientDetailValue>{client.website || '--'}</ClientDetailValue>
              </ClientDetailItem>
            </ClientDetailsGrid>

            {/* Show address fields only for locations (not providers) */}
            {!isProvider && (
              <ClientDetailsGrid style={{ marginTop: '8px' }}>
                <ClientDetailItem>
                  <ClientDetailLabel>Address Line 1:</ClientDetailLabel>
                  <ClientDetailValue>
                    {client.address1 || '--'}
                    {client.address1 && (
                      <CopyIconWrapper
                        title="Copy address"
                        onClick={() =>
                          handleCopyAddress(
                            client.address1,
                            `client-address1-${index}`
                          )
                        }
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect
                            x="9"
                            y="9"
                            width="13"
                            height="13"
                            rx="2"
                            ry="2"
                          />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                        {copiedField === `client-address1-${index}` && (
                          <CopyFeedback>Copied!</CopyFeedback>
                        )}
                      </CopyIconWrapper>
                    )}
                  </ClientDetailValue>
                </ClientDetailItem>
                <ClientDetailItem>
                  <ClientDetailLabel>Address Line 2:</ClientDetailLabel>
                  <ClientDetailValue>
                    {client.address2 || '--'}
                  </ClientDetailValue>
                </ClientDetailItem>
                <ClientDetailItem>
                  <ClientDetailLabel>City:</ClientDetailLabel>
                  <ClientDetailValue>{client.city || '--'}</ClientDetailValue>
                </ClientDetailItem>
                <ClientDetailItem>
                  <ClientDetailLabel>State:</ClientDetailLabel>
                  <ClientDetailValue>{client.state || '--'}</ClientDetailValue>
                </ClientDetailItem>
                <ClientDetailItem>
                  <ClientDetailLabel>Zip:</ClientDetailLabel>
                  <ClientDetailValue>{client.zip || '--'}</ClientDetailValue>
                </ClientDetailItem>
                <ClientDetailItem>
                  <ClientDetailLabel>Country:</ClientDetailLabel>
                  <ClientDetailValue>
                    {client.country || '--'}
                  </ClientDetailValue>
                </ClientDetailItem>
              </ClientDetailsGrid>
            )}

            {isProvider &&
              client.specialties &&
              client.specialties.length > 0 && (
                <ProviderLocationsSection>
                  <ProviderLocationTitle>
                    Specialties ({client.specialties.length})
                  </ProviderLocationTitle>
                  <LocationDetailsGrid>
                    {client.specialties.map((specialty, specIndex) => (
                      <LocationDetailItem
                        key={specialty.specialtyId || specIndex}
                      >
                        <LocationDetailLabel>
                          {specialty.primarySpecialty
                            ? 'Primary Specialty'
                            : 'Specialty'}
                        </LocationDetailLabel>
                        <LocationDetailValue>
                          {specialty.specialtyName || '--'}
                        </LocationDetailValue>
                      </LocationDetailItem>
                    ))}
                  </LocationDetailsGrid>
                </ProviderLocationsSection>
              )}

            {isProvider && (
              <ProviderLocationsSection>
                <ProviderLocationTitle>
                  Associated Locations ({client.providerLocations?.length || 0})
                </ProviderLocationTitle>
                {(client.providerLocations || []).map((location, locIndex) => (
                  <ProviderLocationCard
                    key={location.locationEntityId || locIndex}
                  >
                    <LocationHeader>
                      {location.locationDisplayName ||
                        location.locationInternalName ||
                        `Location ${location.locationEntityId}`}
                      {location.locationEntityId && (
                        <span
                          style={{
                            color: '#666',
                            marginLeft: '8px',
                            fontWeight: 'normal',
                          }}
                        >
                          (ID: {location.locationEntityId})
                        </span>
                      )}
                    </LocationHeader>
                    <LocationDetailsGrid>
                      <LocationDetailItem>
                        <LocationDetailLabel>
                          Address Line 1
                        </LocationDetailLabel>
                        <LocationDetailValue>
                          {location.address1 || '--'}
                          {location.address1 && (
                            <CopyIconWrapper
                              title="Copy address"
                              onClick={() =>
                                handleCopyAddress(
                                  location.address1,
                                  `location-address1-${index}-${locIndex}`
                                )
                              }
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <rect
                                  x="9"
                                  y="9"
                                  width="13"
                                  height="13"
                                  rx="2"
                                  ry="2"
                                />
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                              </svg>
                              {copiedField ===
                                `location-address1-${index}-${locIndex}` && (
                                <CopyFeedback>Copied!</CopyFeedback>
                              )}
                            </CopyIconWrapper>
                          )}
                        </LocationDetailValue>
                      </LocationDetailItem>
                      <LocationDetailItem>
                        <LocationDetailLabel>
                          Address Line 2
                        </LocationDetailLabel>
                        <LocationDetailValue>
                          {location.address2 || '--'}
                        </LocationDetailValue>
                      </LocationDetailItem>
                      <LocationDetailItem>
                        <LocationDetailLabel>City</LocationDetailLabel>
                        <LocationDetailValue>
                          {location.city || '--'}
                        </LocationDetailValue>
                      </LocationDetailItem>
                      <LocationDetailItem>
                        <LocationDetailLabel>State</LocationDetailLabel>
                        <LocationDetailValue>
                          {location.state || '--'}
                        </LocationDetailValue>
                      </LocationDetailItem>
                      <LocationDetailItem>
                        <LocationDetailLabel>Zip</LocationDetailLabel>
                        <LocationDetailValue>
                          {location.zip || '--'}
                        </LocationDetailValue>
                      </LocationDetailItem>
                      <LocationDetailItem>
                        <LocationDetailLabel>Country</LocationDetailLabel>
                        <LocationDetailValue>
                          {location.country || '--'}
                        </LocationDetailValue>
                      </LocationDetailItem>
                      <LocationDetailItem>
                        <LocationDetailLabel>Phone</LocationDetailLabel>
                        <LocationDetailValue>
                          {location.phone || '--'}
                        </LocationDetailValue>
                      </LocationDetailItem>
                    </LocationDetailsGrid>
                  </ProviderLocationCard>
                ))}
              </ProviderLocationsSection>
            )}
          </ClientCard>
        ))}
      </ClientDetailsSection>
    );
  };

  const renderSuggestedSources = () => {
    const { data, isResolved } = suggestedSourcesQuery;
    if (!isResolved) {
      return '...';
    }
    const sources = Object.keys(data);
    return sources.map((source, index) => {
      const sourceUrl = data[source];
      const queryString = `?q=site:${sourceUrl} ${encodeURIComponent(
        entityDetails?.name
      )}`;
      const url = `${GOOGLE_SEARCH_URL}${queryString}`;
      return (
        <SuggestedSource key={source}>
          <SuggestedSourceLink
            href={url}
            target="_blank"
            rel="noreferrer noopener"
          >
            {source}
          </SuggestedSourceLink>
          <span>{`${index < sources.length - 1 ? ', ' : '.'}`}</span>
        </SuggestedSource>
      );
    });
  };

  const renderActions = () => {
    if (isAddingSources) {
      return (
        <ButtonGroup>
          <MozartButton
            outline
            onClick={() => {
              setIsAddingSources(false);
              resetNewSourcesToInitialValue();
            }}
          >
            Cancel
          </MozartButton>
          <MozartButton
            icon="icon-Save"
            disabled={!hasAtLeastOneValidNewSource || addSourcesQuery.isPending}
            onClick={saveNewSources}
          >
            Save
          </MozartButton>
        </ButtonGroup>
      );
    }

    return (
      <ButtonGroup>
        <MozartButton icon="icon-Add" onClick={() => setIsAddingSources(true)}>
          Add Source
        </MozartButton>
        <MozartButton
          icon="icon-TriggerURL"
          disabled={urlFinder.query.isPending}
          onClick={urlFinder.openModal}
        >
          Trigger URL Finder
        </MozartButton>
        <MozartButton outline onClick={() => history.push('/entity-listing')}>
          Back to Entity Listing
        </MozartButton>
      </ButtonGroup>
    );
  };

  return (
    <Layout title="Verify Sources" actions={renderActions()}>
      {entityQuery.isIdle ||
      entityQuery.isPending ||
      verifyQuery.isPending ||
      isClientDetailsLoading ? (
        <MozartLoading />
      ) : entityQuery.isRejected ? (
        <p>There was an error loading the entity details.</p>
      ) : (
        <>
          <Card>
            <CardHeader>
              <EntityDetailsHeaderWrapper>
                <EntityDetails>
                  <Detail width="150px">
                    <DetailLabel>Entity ID</DetailLabel>
                    <DetailValue>{entityDetails.id}</DetailValue>
                  </Detail>
                  <Detail width="150px">
                    <DetailLabel>NPI</DetailLabel>
                    <DetailValue>{entityDetails.code}</DetailValue>
                  </Detail>

                  <Detail width="120px">
                    <DetailLabel>Entity Name</DetailLabel>
                    <DetailValue>{entityDetails.name}</DetailValue>
                  </Detail>

                  <Detail width="120px">
                    <DetailLabel>Last Crawled On</DetailLabel>
                    <DetailValue>
                      {entityDetails.lastCrawledOn
                        ? format(
                            new Date(entityDetails.lastCrawledOn),
                            DATE_FORMAT
                          )
                        : '--'}
                    </DetailValue>
                  </Detail>

                  <Detail width="120px">
                    <DetailLabel>Last URL Verified On</DetailLabel>
                    <DetailValue>
                      {entityDetails.lastUrlVerifiedOn
                        ? format(
                            new Date(entityDetails.lastUrlVerifiedOn),
                            DATE_FORMAT
                          )
                        : '--'}
                    </DetailValue>
                  </Detail>
                </EntityDetails>
                <VerifyButtonWrapper>
                  <MozartButton
                    title={verifyButtonTitle}
                    disabled={isEntityVerified}
                    onClick={() => setShowVerifyEntityModal(true)}
                  >
                    Verify Entity
                  </MozartButton>
                </VerifyButtonWrapper>
              </EntityDetailsHeaderWrapper>
            </CardHeader>
            <CustomCardContent>
              <EntityDetails>
                <Detail width="150px">
                  <DetailLabel>Address Line 1</DetailLabel>
                  <DetailValue>
                    {entityDetails.address?.address1 || '--'}
                    {entityDetails.address?.address1 && (
                      <CopyIconWrapper
                        title="Copy address"
                        onClick={() =>
                          handleCopyAddress(
                            entityDetails.address.address1,
                            'entity-address1'
                          )
                        }
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect
                            x="9"
                            y="9"
                            width="13"
                            height="13"
                            rx="2"
                            ry="2"
                          />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                        {copiedField === 'entity-address1' && (
                          <CopyFeedback>Copied!</CopyFeedback>
                        )}
                      </CopyIconWrapper>
                    )}
                  </DetailValue>
                </Detail>

                <Detail width="120px">
                  <DetailLabel>Address Line 2</DetailLabel>
                  <DetailValue>
                    {entityDetails.address?.address2 || '--'}
                  </DetailValue>
                </Detail>

                <Detail width="120px">
                  <DetailLabel>City</DetailLabel>
                  <DetailValue>
                    {entityDetails.address?.city || '--'}
                  </DetailValue>
                </Detail>

                <Detail width="120px">
                  <DetailLabel>State</DetailLabel>
                  <DetailValue>
                    {entityDetails.address?.state || '--'}
                  </DetailValue>
                </Detail>

                <Detail width="120px">
                  <DetailLabel>Zip</DetailLabel>
                  <DetailValue>
                    {entityDetails.address?.zip || '--'}
                  </DetailValue>
                </Detail>

                <Detail width="120px">
                  <DetailLabel>Specialty</DetailLabel>
                  <DetailValue>{entityDetails.specialty || '--'}</DetailValue>
                </Detail>
              </EntityDetails>

              <Divider />

              <p>
                The suggested sources for which harvesters to be added are{' '}
                {renderSuggestedSources()}
              </p>

              {renderClientDetails()}
            </CustomCardContent>
          </Card>

          <CustomCard>
            <CustomCardContent padding="0">
              <SourcesTable.Table id="verify-sources-table">
                <SourcesTable.Head>{getHeaders()}</SourcesTable.Head>
                <SourcesTable.Body>{getRows()}</SourcesTable.Body>
              </SourcesTable.Table>

              {entitySources.length > 0 && (
                <SelectedActions>
                  <ButtonGroup>
                    <MozartButton
                      outline
                      disabled={checkboxes.noneChecked}
                      onClick={verifyCheckedSources}
                    >
                      Verify Selected
                    </MozartButton>
                    <MozartButton
                      outline
                      disabled={checkboxes.noneChecked}
                      onClick={() =>
                        setDeletableHarvesterIds(checkedHarvesterIds)
                      }
                    >
                      Delete Selected
                    </MozartButton>
                  </ButtonGroup>
                </SelectedActions>
              )}
            </CustomCardContent>
          </CustomCard>

          {entitySources.length > 0 && (
            <ListLabel>{`Showing ${entitySources.length} of ${entitySources.length}`}</ListLabel>
          )}

          <VerifyEntityModal
            show={showVerifyEntityModal}
            entityId={entityDetails?.id}
            onClose={handleVerifyEntityClose}
          />

          <UrlFinderModal
            withConfirmation
            show={urlFinder.showModal}
            isConfirmed={urlFinder.isConfirmed}
            status={urlFinder.query.status}
            onConfirm={handleTriggerUrlFinderConfirm}
            onClose={urlFinder.closeModal}
          />

          <ReviewsModal
            source={activeSourceReviews}
            onClose={handleReviewsModalClose}
          />

          <HistoryModal
            source={activeSourceHistory}
            onClose={() => setActiveSourceHistory(null)}
          />

          <DeleteModal
            harvesterIds={deletableHaversterIds}
            onCancel={() => setDeletableHarvesterIds([])}
            onConfirm={handleDeleteConfirm}
          />

          <AddSourcesErrorsModal
            errors={newSourcesErrors}
            onClose={resetAddSources}
          />
        </>
      )}
    </Layout>
  );
};

export default VerifySources;
